/**
 * Page-side bridge that turns incoming push events into:
 *   1. An in-app toast (via `pushToastStore`).
 *   2. A React Query invalidation of the in-app notifications list (so the
 *      bell badge & the /notifications screen refresh immediately).
 *
 * Drop one `usePushMessageBridge()` call inside the authenticated app shell
 * — the hook is a no-op for unauthenticated visitors.
 *
 * Source of events: `PUSH_MESSAGE_EVENT` dispatched by `pushService` when:
 *   - the FCM SDK delivers a foreground `onMessage` payload, OR
 *   - the service worker forwards a background-notification *click*.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import type {
  NotificationPendingSummary,
  NotificationsResponse,
} from '@/features/notifications/api';
import type { NotificationActor } from '@/features/notifications/types';
import { useAuthStore } from '@/store/authStore';
import { buildInAppNotification } from './parseInAppPush';
import { usePushToastStore } from './pushToastStore';
import { PUSH_MESSAGE_EVENT, type IncomingPushMessage } from './types';

export type PushPendingRequestKind = 'follow' | 'board' | null;

export function getPushPendingRequestKind(type: string | undefined): PushPendingRequestKind {
  if (type === 'Follow' || type === 'FollowUserRequest') return 'follow';
  if (type === 'JoinBoard' || type === 'AdminInvite') return 'board';
  return null;
}

function actorFromPush(notification: ReturnType<typeof buildInAppNotification>): NotificationActor {
  const actor = notification.payload.whoFiredUserBasicDetail;
  return {
    id: actor?.id ?? 0,
    name: [actor?.name, actor?.surName].filter(Boolean).join(' ').trim() || notification.title,
    initials: notification.initials,
    username: actor?.username,
    avatarUrl: notification.avatarUrl,
  };
}

function incrementPendingRequest(
  current: NotificationsResponse | undefined,
  kind: Exclude<PushPendingRequestKind, null>,
  notification: ReturnType<typeof buildInAppNotification>,
): NotificationsResponse | undefined {
  if (!current) return current;
  const actor = actorFromPush(notification);
  const currentSummary = kind === 'follow' ? current.joinFollowRequest : current.joinBoardRequest;
  const summary: NotificationPendingSummary = {
    count: (currentSummary?.count ?? 0) + 1,
    latestActor: actor,
    createdAt: notification.receivedAt,
  };

  return kind === 'follow'
    ? {
        ...current,
        pending: {
          ...current.pending,
          joinFollowRequestCount: current.pending.joinFollowRequestCount + 1,
        },
        joinFollowRequest: summary,
      }
    : {
        ...current,
        pending: {
          ...current.pending,
          joinBoardRequestCount: current.pending.joinBoardRequestCount + 1,
        },
        joinBoardRequest: summary,
      };
}

export function usePushMessageBridge(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const enqueue = usePushToastStore((s) => s.enqueue);
  const queryClient = useQueryClient();
  const countedPushKeys = useRef(new Set<string>());

  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<IncomingPushMessage>).detail;
      if (!detail) return;

      try {
        // 1. Surface the toast — `parseInAppPush` handles the nested JSON
        //    shape and resolves the click target per backend `type`.
        const notification = buildInAppNotification(detail);
        if (import.meta.env.DEV) {
          console.info(
            `[push-bridge] toast queued ▸ ${notification.payload.type ?? 'unknown'}`,
            notification,
          );
        }
        enqueue(notification);

        const pendingKind = getPushPendingRequestKind(notification.payload.type);
        if (pendingKind && !countedPushKeys.current.has(notification.key)) {
          countedPushKeys.current.add(notification.key);
          if (countedPushKeys.current.size > 200) {
            const oldest = countedPushKeys.current.values().next().value;
            if (oldest) countedPushKeys.current.delete(oldest);
          }
          queryClient.setQueriesData<NotificationsResponse>(
            { queryKey: [...queryKeys.notifications.all, 'you'] },
            (current) => incrementPendingRequest(current, pendingKind, notification),
          );
        }

        // 2. Refresh the bell + the /notifications screen. We only
        //    invalidate when the push has a recognisable in-app type so
        //    debug pushes (e.g. backend smoke tests) don't trigger noisy
        //    refetches.
        const knownType = typeof notification.payload.type === 'string';
        if (knownType) {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.all,
            // Pending-request badges are patched optimistically above. Mark
            // their queries stale without immediately replacing the new count
            // with an eventually-consistent notification-service response.
            refetchType: pendingKind ? 'none' : 'active',
          });

          if (pendingKind === 'follow') {
            void queryClient.invalidateQueries({
              queryKey: [...queryKeys.notifications.all, 'follow-requests'],
              refetchType: 'active',
            });
            // A Follow push can represent a new request or an accepted follow.
            // Refresh profile/social totals so follower and following counts do
            // not remain stale after either event.
            void queryClient.invalidateQueries({ queryKey: ['profile'], refetchType: 'active' });
          } else if (pendingKind === 'board') {
            void queryClient.invalidateQueries({
              queryKey: [...queryKeys.notifications.all, 'join-board-requests'],
              refetchType: 'active',
            });
          }
        }
      } catch (err) {
        // Never let a single malformed payload tear down the listener —
        // surface the error in DEV so we can fix the parser.
        if (import.meta.env.DEV) {
          console.warn('[push-bridge] handler threw', err, detail);
        }
      }
    };

    window.addEventListener(PUSH_MESSAGE_EVENT, handler);
    if (import.meta.env.DEV) {
      console.info('[push-bridge] listening for', PUSH_MESSAGE_EVENT);
    }
    return () => window.removeEventListener(PUSH_MESSAGE_EVENT, handler);
  }, [isAuthenticated, enqueue, queryClient]);
}
